import type { FastifyInstance } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import { AIController } from './ai.controller.js'
import { AIService } from './ai.service.js'
import { AIRepository } from './ai.repository.js'
import { getPOICostDashboard, getPOIBudgetSummary } from './controllers/poi-cost-dashboard.controller.js'
import type { RedisClient } from '../../core/redis/client.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'
import { checkAIQuota, getAIUsage } from './ai.middleware.js'
import { validateRequest } from '../../middleware/validate.middleware.js'
import {
  SuggestPOIsRequestSchema,
  EnhancePlanRequestSchema,
  ContextualSuggestionsRequestSchema,
  DashboardSuggestionRequestSchema
} from './ai.schemas.js'
import { db } from '../../core/database/firebase.js'
import { redis } from '../../core/database/redis.js'

interface AIRoutesOptions {
  controller?: AIController
  service?: AIService
  repository?: AIRepository
  firestore?: Firestore
  redisClient?: RedisClient
}

export async function aiRoutes(app: FastifyInstance, options: AIRoutesOptions = {}) {
  const {
    controller: injectedController,
    service,
    repository,
    firestore,
    redisClient
  } = options

  const minute = '1 minute'
  const rateConfig = (max: number, timeWindow: string = minute) => ({
    config: {
      rateLimit: { max, timeWindow }
    }
  })

  const controller =
    injectedController ??
    new AIController(
      service ?? new AIService(),
      repository ?? new AIRepository(),
      firestore ?? db,
      redisClient ?? redis
    )

  // POI Suggestions endpoint (with quota check)
  app.post(
    '/suggest-pois',
    { 
      ...rateConfig(10),
      preHandler: [authMiddleware, checkAIQuota, validateRequest(SuggestPOIsRequestSchema)]
    },
    async (request, reply) => controller.suggestPOIs(request as any, reply)
  )

  // POI Suggestions STREAMING endpoint (Server-Sent Events)
  app.post(
    '/suggest-pois/stream',
    { 
      ...rateConfig(10),
      preHandler: [authMiddleware, checkAIQuota, validateRequest(SuggestPOIsRequestSchema)]
    },
    async (request, reply) => controller.suggestPOIsStream(request as any, reply)
  )

  // Plan Enhancement endpoint (with quota check)
  app.post(
    '/enhance-plan',
    { 
      ...rateConfig(10),
      preHandler: [authMiddleware, checkAIQuota, validateRequest(EnhancePlanRequestSchema)]
    },
    async (request, reply) => controller.enhancePlan(request as any, reply)
  )

  // Contextual suggestions endpoint (no quota, DB only)
  app.post(
    '/contextual-suggestions',
    { 
      ...rateConfig(10),
      preHandler: [authMiddleware, validateRequest(ContextualSuggestionsRequestSchema)]
    },
    async (request, reply) => controller.getContextualSuggestions(request as any, reply)
  )

  app.post(
    '/dashboard-suggestions',
    {
      ...rateConfig(10),
      preHandler: [authMiddleware, validateRequest(DashboardSuggestionRequestSchema)]
    },
    async (request, reply) => controller.getDashboardSuggestions(request as any, reply)
  )

  // Job status endpoint
  app.get(
    '/jobs/:jobId',
    { 
      ...rateConfig(60),
      preHandler: [authMiddleware] 
    },
    async (request, reply) => controller.getJobStatus(request as any, reply)
  )

  // Queue status endpoint (admin/monitoring)
  app.get(
    '/queue/status',
    { 
      ...rateConfig(60),
      preHandler: [authMiddleware] 
    },
    async (request, reply) => controller.getQueueStatus(request as any, reply)
  )

  // Usage endpoint - get user's AI usage stats
  app.get(
    '/usage',
    { 
      ...rateConfig(60),
      preHandler: [authMiddleware] 
    },
    getAIUsage
  )

  // Aggregated insights (usage + queue + analytics)
  app.get(
    '/insights',
    { 
      ...rateConfig(60),
      preHandler: [authMiddleware] 
    },
    async (request, reply) => controller.getInsights(request as any, reply)
  )

  // Viewport POIs endpoint - optimized for map display with 50k+ POIs
  // No quota needed - reads from database only
  app.get(
    '/pois/viewport',
    { 
      ...rateConfig(30), // Allow frequent calls for map panning
      preHandler: [authMiddleware]
    },
    async (request, reply) => controller.getPOIsInViewport(request as any, reply)
  )

  // Get full POI details by ID (on-demand, for clicks)
  app.get(
    '/pois/:poiId',
    { 
      ...rateConfig(60),
      preHandler: [authMiddleware]
    },
    async (request, reply) => controller.getPOIDetails(request as any, reply)
  )

  // Two-phase enrichment: parse → generate
  // Phase 1: Parse text into segments
  // Phase 2: Generate stages with POI search
  app.post(
    '/enrich-text',
    { 
      ...rateConfig(20),
      preHandler: [authMiddleware]
    },
    async (request, reply) => controller.enrichText(request as any, reply)
  )

  // Debug endpoint - dev-only, shows geohash breakdown
  app.get(
    '/pois/viewport/debug',
    { 
      ...rateConfig(30),
      preHandler: [authMiddleware]
    },
    async (request, reply) => controller.debugViewportPOIs(request as any, reply)
  )

  // POI Cost Dashboard endpoints (admin only)
  app.get(
    '/costs/pois/dashboard',
    { 
      ...rateConfig(10),
      preHandler: [authMiddleware]
    },
    getPOICostDashboard
  )

  app.get(
    '/costs/pois/budget',
    { 
      ...rateConfig(10),
      preHandler: [authMiddleware]
    },
    getPOIBudgetSummary
  )
}
